import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaBookOpen,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaGraduationCap,
} from "react-icons/fa";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import PageLoader from "../../components/PageLoader";
import { supabase } from "../../supabaseClient";

function formatDateTime(value) {
  if (!value) return "No due date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No due date";
  return date.toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getProgress(progressMap, assignmentId) {
  return progressMap[assignmentId] || {
    status: "Assigned",
    best_score: null,
    completed_at: null,
  };
}

function dueState(assignment, progress) {
  if (progress?.status === "Completed") {
    return { key: "complete", label: "Completed" };
  }
  if (!assignment?.due_at) {
    return { key: "none", label: "No due date" };
  }
  const due = new Date(assignment.due_at).getTime();
  const diff = due - Date.now();
  if (diff < 0) return { key: "overdue", label: "Overdue" };
  if (diff <= 24 * 60 * 60 * 1000) return { key: "urgent", label: "Due within 24 hours" };
  if (diff <= 7 * 24 * 60 * 60 * 1000) return { key: "soon", label: "Due soon" };
  return { key: "future", label: "Assigned" };
}

export default function ELearning({ session, profile }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [selectedId, setSelectedId] = useState("");
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [quizLoading, setQuizLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    if (!session?.user?.id) return;
    setLoading(true);
    setFeedback(null);

    const [assignmentResult, progressResult] = await Promise.all([
      supabase
        .from("e_learning_assignments")
        .select(`
          id,
          module_id,
          user_id,
          due_at,
          assigned_at,
          assigned_via,
          assigned_role,
          revoked_at,
          module:e_learning_modules!e_learning_assignments_module_id_fkey(
            id,
            title,
            description,
            category,
            content_url,
            content_notes,
            mandatory,
            quiz_enabled,
            passing_score,
            estimated_minutes,
            active
          )
        `)
        .eq("user_id", session.user.id)
        .is("revoked_at", null)
        .order("due_at", { ascending: true, nullsFirst: false }),
      supabase
        .from("e_learning_progress")
        .select("assignment_id, status, started_at, completed_at, best_score, last_attempt_at")
        .eq("user_id", session.user.id),
    ]);

    if (assignmentResult.error || progressResult.error) {
      console.error("Unable to load E-Learning:", assignmentResult.error || progressResult.error);
      setFeedback({
        type: "error",
        text: "E-Learning could not be loaded. Management may need to run ADD_STAFF_ELEARNING.sql.",
      });
      setAssignments([]);
      setProgressMap({});
      setLoading(false);
      return;
    }

    const nextAssignments = assignmentResult.data || [];
    const nextProgress = Object.fromEntries(
      (progressResult.data || []).map((row) => [row.assignment_id, row]),
    );
    setAssignments(nextAssignments);
    setProgressMap(nextProgress);

    const requested = new URLSearchParams(location.search).get("assignment");
    const requestedExists = nextAssignments.some((row) => row.id === requested);
    const firstOutstanding = nextAssignments.find(
      (row) => getProgress(nextProgress, row.id).status !== "Completed",
    );
    const nextSelected = requestedExists
      ? requested
      : selectedId && nextAssignments.some((row) => row.id === selectedId)
        ? selectedId
        : firstOutstanding?.id || nextAssignments[0]?.id || "";
    setSelectedId(nextSelected);
    setLoading(false);
  }, [location.search, selectedId, session?.user?.id]);

  useEffect(() => {
    void load();
  }, [session?.user?.id]);

  const selected = assignments.find((row) => row.id === selectedId) || null;
  const selectedProgress = selected ? getProgress(progressMap, selected.id) : null;

  useEffect(() => {
    let active = true;
    setAnswers({});
    setQuizQuestions([]);
    if (!selected?.id) return undefined;

    const openAssignment = async () => {
      await supabase.rpc("start_my_elearning", {
        target_assignment_id: selected.id,
      });

      if (!selected.module?.quiz_enabled) return;
      setQuizLoading(true);
      const { data, error } = await supabase.rpc("get_my_elearning_quiz", {
        target_assignment_id: selected.id,
      });
      if (!active) return;
      if (error) {
        console.error("Unable to load E-Learning quiz:", error);
        setFeedback({ type: "error", text: "This quiz could not be loaded." });
      } else {
        setQuizQuestions(data || []);
      }
      setQuizLoading(false);
    };

    void openAssignment();
    return () => {
      active = false;
    };
  }, [selected?.id, selected?.module?.quiz_enabled]);

  const mandatory = useMemo(
    () => assignments.filter((row) => row.module?.mandatory),
    [assignments],
  );
  const development = useMemo(
    () => assignments.filter((row) => !row.module?.mandatory),
    [assignments],
  );
  const outstandingMandatory = mandatory.filter(
    (row) => getProgress(progressMap, row.id).status !== "Completed",
  );
  const dueSoon = assignments.filter((row) => {
    const state = dueState(row, getProgress(progressMap, row.id));
    return ["overdue", "urgent", "soon"].includes(state.key);
  });

  function selectAssignment(id) {
    setSelectedId(id);
    const params = new URLSearchParams(location.search);
    params.set("assignment", id);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }

  async function submitQuiz(event) {
    event.preventDefault();
    if (!selected) return;
    if (quizQuestions.length === 0) {
      setFeedback({ type: "error", text: "This quiz has no questions." });
      return;
    }
    if (quizQuestions.some((question) => answers[question.question_id] === undefined)) {
      setFeedback({ type: "error", text: "Answer every quiz question before submitting." });
      return;
    }

    setWorking(true);
    setFeedback(null);
    const { data, error } = await supabase.rpc("submit_my_elearning_quiz", {
      target_assignment_id: selected.id,
      submitted_answers: answers,
    });
    setWorking(false);

    if (error) {
      console.error("Quiz submission failed:", error);
      setFeedback({ type: "error", text: error.message || "The quiz could not be submitted." });
      return;
    }

    const result = Array.isArray(data) ? data[0] : data;
    if (result?.passed) {
      setFeedback({
        type: "success",
        text: `Passed with ${result.score}%. This learning is now complete.`,
      });
    } else {
      setFeedback({
        type: "warning",
        text: `Score: ${result?.score ?? 0}%. You need ${selected.module?.passing_score || 80}% to pass. You can retry the quiz.`,
      });
    }
    await load();
  }

  async function completeWithoutQuiz() {
    if (!selected) return;
    setWorking(true);
    setFeedback(null);
    const { error } = await supabase.rpc("complete_my_elearning_without_quiz", {
      target_assignment_id: selected.id,
    });
    setWorking(false);
    if (error) {
      setFeedback({ type: "error", text: error.message || "Learning could not be completed." });
      return;
    }
    setFeedback({ type: "success", text: "Learning marked as complete." });
    await load();
  }

  if (loading) return <PageLoader label="Loading E-Learning" />;

  return (
    <div className="pguh-page-shell">
      <Header session={session} profile={profile} />
      <main className="pguh-main">
        <div className="nhsuk-width-container pguh-elearning-page">
          <section className="pguh-dashboard-hero pguh-elearning-hero">
            <div>
              <span className="pguh-eyebrow">Learning & development</span>
              <h1 className="nhsuk-heading-xl">My E-Learning</h1>
              <p className="nhsuk-body-l">
                Complete mandatory training and optional development assigned by Management.
              </p>
            </div>
            <div className="pguh-dashboard-identity">
              <span>Mandatory outstanding</span>
              <strong>{outstandingMandatory.length}</strong>
              <small>{dueSoon.length} due soon / overdue</small>
            </div>
          </section>

          {feedback && (
            <div
              className={`pguh-elearning-feedback pguh-elearning-feedback--${feedback.type}`}
              role="status"
            >
              {feedback.text}
            </div>
          )}

          <section className="pguh-dashboard-stats" aria-label="E-Learning summary">
            <div className="pguh-stat-card">
              <span>Mandatory</span>
              <strong>{mandatory.length}</strong>
              <small>{outstandingMandatory.length} outstanding</small>
            </div>
            <div className="pguh-stat-card">
              <span>Development</span>
              <strong>{development.length}</strong>
              <small>Non-mandatory learning</small>
            </div>
            <div className="pguh-stat-card">
              <span>Due soon</span>
              <strong>{dueSoon.length}</strong>
              <Link to="/intranet/calendar">View calendar</Link>
            </div>
          </section>

          <div className="pguh-elearning-layout">
            <aside className="pguh-panel pguh-elearning-list" aria-label="Assigned learning">
              <div className="pguh-panel__header">
                <div>
                  <span className="pguh-eyebrow">Assigned to you</span>
                  <h2 className="nhsuk-heading-m">Learning modules</h2>
                </div>
              </div>

              {assignments.length === 0 ? (
                <div className="pguh-empty-state">
                  <FaGraduationCap aria-hidden="true" />
                  <h3>No learning assigned</h3>
                  <p>Management-assigned courses will appear here.</p>
                </div>
              ) : (
                <div className="pguh-elearning-assignment-list">
                  {assignments.map((assignment) => {
                    const progress = getProgress(progressMap, assignment.id);
                    const state = dueState(assignment, progress);
                    return (
                      <button
                        key={assignment.id}
                        type="button"
                        className={`pguh-elearning-assignment${selectedId === assignment.id ? " is-selected" : ""}`}
                        onClick={() => selectAssignment(assignment.id)}
                      >
                        <div>
                          <span className="pguh-elearning-assignment__type">
                            {assignment.module?.mandatory ? "Mandatory" : "Development"}
                          </span>
                          <strong>{assignment.module?.title || "Untitled learning"}</strong>
                          <small>{assignment.module?.category || "General"}</small>
                        </div>
                        <span className={`pguh-learning-status pguh-learning-status--${state.key}`}>
                          {state.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </aside>

            <section className="pguh-panel pguh-elearning-detail">
              {!selected ? (
                <div className="pguh-empty-state">
                  <FaBookOpen aria-hidden="true" />
                  <h2>Select a learning module</h2>
                  <p>Choose a module to view its content and completion requirements.</p>
                </div>
              ) : (
                <>
                  <div className="pguh-elearning-detail__heading">
                    <div>
                      <span className="pguh-eyebrow">{selected.module?.category || "General"}</span>
                      <h2 className="nhsuk-heading-l">{selected.module?.title}</h2>
                      <div className="pguh-elearning-badges">
                        <span className={selected.module?.mandatory ? "is-mandatory" : ""}>
                          {selected.module?.mandatory ? "Mandatory" : "Non-mandatory"}
                        </span>
                        {selected.module?.estimated_minutes && (
                          <span><FaClock aria-hidden="true" /> {selected.module.estimated_minutes} min</span>
                        )}
                        {selected.module?.quiz_enabled && (
                          <span>Quiz · pass mark {selected.module.passing_score}%</span>
                        )}
                      </div>
                    </div>
                    <span className={`pguh-learning-status pguh-learning-status--${dueState(selected, selectedProgress).key}`}>
                      {dueState(selected, selectedProgress).label}
                    </span>
                  </div>

                  <dl className="pguh-compact-details pguh-elearning-details-grid">
                    <div><dt>Due</dt><dd>{formatDateTime(selected.due_at)}</dd></div>
                    <div><dt>Status</dt><dd>{selectedProgress?.status || "Assigned"}</dd></div>
                    <div><dt>Best score</dt><dd>{selectedProgress?.best_score == null ? "Not attempted" : `${selectedProgress.best_score}%`}</dd></div>
                    <div><dt>Assigned</dt><dd>{formatDateTime(selected.assigned_at)}</dd></div>
                  </dl>

                  {selected.module?.description && (
                    <div className="pguh-elearning-copy">
                      <h3 className="nhsuk-heading-s">About this learning</h3>
                      <p>{selected.module.description}</p>
                    </div>
                  )}

                  {selected.module?.content_notes && (
                    <div className="pguh-elearning-copy">
                      <h3 className="nhsuk-heading-s">Learning instructions</h3>
                      <p>{selected.module.content_notes}</p>
                    </div>
                  )}

                  {selected.module?.content_url && (
                    <a
                      className="nhsuk-button nhsuk-button--secondary pguh-elearning-link-button"
                      href={selected.module.content_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open learning resource <FaExternalLinkAlt aria-hidden="true" />
                    </a>
                  )}

                  {selectedProgress?.status === "Completed" ? (
                    <div className="pguh-elearning-complete-panel">
                      <FaCheckCircle aria-hidden="true" />
                      <div>
                        <strong>Learning completed</strong>
                        <p>
                          Completed {selectedProgress.completed_at ? formatDateTime(selectedProgress.completed_at) : "successfully"}.
                        </p>
                      </div>
                    </div>
                  ) : selected.module?.quiz_enabled ? (
                    <form className="pguh-elearning-quiz" onSubmit={submitQuiz}>
                      <div className="pguh-section-heading">
                        <div>
                          <span className="pguh-eyebrow">Assessment</span>
                          <h3 className="nhsuk-heading-m">
                            {selected.module?.mandatory ? "Mandatory quiz" : "Quiz"}
                          </h3>
                          <p>
                            You must achieve at least {selected.module?.passing_score || 80}% to complete this learning.
                          </p>
                        </div>
                      </div>

                      {quizLoading ? (
                        <p>Loading quiz…</p>
                      ) : quizQuestions.length === 0 ? (
                        <div className="nhsuk-warning-callout">
                          <h4 className="nhsuk-warning-callout__label">Quiz unavailable</h4>
                          <p>Management has not added quiz questions yet.</p>
                        </div>
                      ) : (
                        quizQuestions.map((question, index) => {
                          const options = Array.isArray(question.options) ? question.options : [];
                          return (
                            <fieldset className="nhsuk-fieldset pguh-elearning-question" key={question.question_id}>
                              <legend className="nhsuk-fieldset__legend nhsuk-fieldset__legend--s">
                                {index + 1}. {question.question_text}
                              </legend>
                              <div className="nhsuk-radios">
                                {options.map((option, optionIndex) => (
                                  <div className="nhsuk-radios__item" key={`${question.question_id}-${optionIndex}`}>
                                    <input
                                      className="nhsuk-radios__input"
                                      id={`${question.question_id}-${optionIndex}`}
                                      type="radio"
                                      name={`question-${question.question_id}`}
                                      checked={answers[question.question_id] === optionIndex}
                                      onChange={() =>
                                        setAnswers((current) => ({
                                          ...current,
                                          [question.question_id]: optionIndex,
                                        }))
                                      }
                                    />
                                    <label className="nhsuk-label nhsuk-radios__label" htmlFor={`${question.question_id}-${optionIndex}`}>
                                      {option}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </fieldset>
                          );
                        })
                      )}

                      <button className="nhsuk-button" type="submit" disabled={working || quizLoading || quizQuestions.length === 0}>
                        {working ? "Marking quiz…" : "Submit quiz"}
                      </button>
                    </form>
                  ) : (
                    <div className="pguh-elearning-manual-complete">
                      <h3 className="nhsuk-heading-s">Finish this learning</h3>
                      <p>When you have reviewed the learning material, mark it as complete.</p>
                      <button className="nhsuk-button" type="button" onClick={completeWithoutQuiz} disabled={working}>
                        {working ? "Saving…" : "Mark learning complete"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
